/**
 * Web-Mercator "slippy map" maths, ported 1:1 from
 * wear/src/main/java/com/example/myrangefinder/ui/screens/round/satellite/SlippyMap.kt.
 *
 * OSM/ESRI convention: tile (0,0) is the top-left of the world at zoom 0, 256 px tiles.
 */

import type { GeoPoint } from './geoPoint';

export const TILE_SIZE = 256;

/** Zoom used by the hole viewer. Matches the watch's TILE_ZOOM. */
export const HOLE_TILE_ZOOM = 18;

/** World-pixel coordinates of a (lat, lng) point at `zoom`. */
export function project(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom;
  const x = ((lng + 180) / 360) * n * TILE_SIZE;
  // Clamped to the Mercator limit — the projection diverges at the poles.
  const latRad = (Math.max(-85.0511, Math.min(85.0511, lat)) * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n * TILE_SIZE;
  return { x, y };
}

export function projectGeo(p: GeoPoint, zoom: number): { x: number; y: number } {
  return project(p.latitude, p.longitude, zoom);
}

/** Inverse of `project`. */
export function unproject(worldX: number, worldY: number, zoom: number): GeoPoint {
  const n = 2 ** zoom;
  const longitude = (worldX / (TILE_SIZE * n)) * 360 - 180;
  const latitude =
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * worldY) / (TILE_SIZE * n)))) * 180) / Math.PI;
  return { longitude, latitude };
}

export interface TileCoord {
  z: number;
  x: number;
  y: number;
}

/**
 * The tiles covering a bounding box, with a 1-tile margin on each side so that
 * rotating the view does not expose the edge.
 */
export function tilesCovering(
  minLat: number,
  maxLat: number,
  minLng: number,
  maxLng: number,
  zoom: number,
): TileCoord[] {
  // Top-left of the bbox is (maxLat, minLng) — Y is inverted in screen space.
  const topLeft = project(maxLat, minLng, zoom);
  const bottomRight = project(minLat, maxLng, zoom);

  const tileMinX = Math.floor(topLeft.x / TILE_SIZE) - 1;
  const tileMaxX = Math.floor(bottomRight.x / TILE_SIZE) + 1;
  const tileMinY = Math.floor(topLeft.y / TILE_SIZE) - 1;
  const tileMaxY = Math.floor(bottomRight.y / TILE_SIZE) + 1;

  const maxTile = (1 << zoom) - 1;
  const out: TileCoord[] = [];
  for (let ty = tileMinY; ty <= tileMaxY; ty++) {
    if (ty < 0 || ty > maxTile) continue;
    for (let tx = tileMinX; tx <= tileMaxX; tx++) {
      if (tx < 0 || tx > maxTile) continue;
      out.push({ z: zoom, x: tx, y: ty });
    }
  }
  return out;
}

/**
 * ESRI World Imagery — the same source the watch uses. No API key, and the host sends
 * `Access-Control-Allow-Origin: *`, so tiles load straight into a canvas.
 *
 * NOTE the path order is {z}/{y}/{x} — ESRI, unlike OSM, puts row before column.
 * Attribution is mandatory and is rendered on-screen wherever tiles appear.
 */
export function esriTileUrl({ z, x, y }: TileCoord): string {
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
}

export const ESRI_ATTRIBUTION = 'Imagens: Esri, Maxar, Earthstar Geographics';

/** The single tile containing a point — enough for a header texture. */
export function tileContaining(p: GeoPoint, zoom: number): TileCoord {
  const { x, y } = projectGeo(p, zoom);
  return { z: zoom, x: Math.floor(x / TILE_SIZE), y: Math.floor(y / TILE_SIZE) };
}
